import React, { useEffect, useId, useRef, useState } from 'react';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import DialpadRoundedIcon from '@mui/icons-material/DialpadRounded';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { CountryCode, parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { upsertParticipant } from 'store/callScribe';
import { CallScribeAccessSelector, CallScribeCallsByTicketSelector } from 'store/callScribe/selectors';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import { addParticipant } from 'services/callScribe/conference';
import { IAddCallParticipantRequest, ICallScribeCallListItem } from 'services/callScribe/types';
import CountryDialField, { numberHelper } from 'components/CallScribe/Home/CountryDialField';
import { useDialNumber } from 'components/CallScribe/Home/useDialNumber';
import Keypad from 'components/CallScribe/Dialer/Keypad';
import PeopleList, { flattenGroups, personOptionId, stepActive } from 'components/CallScribe/Conference/PeopleList';
import { personCallName, personShortTitle } from 'components/CallScribe/Conference/PersonRow';
import {
  IPerson,
  TPeopleMode,
  usePeopleDirectory,
} from 'components/CallScribe/Conference/usePeopleDirectory';
import useAddPersonOverlayStyles from 'components/CallScribe/Conference/addPersonOverlayStyles';
import ActionBar from 'components/CallScribe/ui/ActionBar';
import { TFieldHelperTone } from 'components/CallScribe/ui/Field';
import Sheet from 'components/CallScribe/ui/Sheet';

export const ADD_PERSON_FALLBACK_ERROR = "Couldn't add that person. Try again.";

export const SHORT_SHEET_PX = 640;

export interface IDialPick {
  to: string;
  contactExternalId?: string;
  companyExternalId?: string;
  contactName?: string;
}

export interface IAddPersonOverlayProps {
  open: boolean;
  callId?: number;
  crmSourceId?: number | null;
  suggestCrmSourceId?: number | null;
  companyId?: string | number | null;
  defaultCountry?: CountryCode;
  onClose: () => void;
  mode?: TPeopleMode;
  ticketPeople?: IPerson[];
  recentCalls?: ICallScribeCallListItem[];
  onDial?: (pick: IDialPick) => void;
}

const COPY: Record<TPeopleMode, {
  title: string;
  idle: string;
  loading: string;
  noMatchNumber: string;
  noMatchNumberBody: string;
  noMatchName: string;
}> = {
  add: {
    title: 'Add to this call',
    idle: 'Search by name, or dial a number.',
    loading: 'Loading your team and contacts…',
    noMatchNumber: 'No teammate or contact has this number',
    noMatchNumberBody: 'You can still add it to the call as a new participant.',
    noMatchName: 'Only teammates and contacts with a phone number are listed.',
  },
  dial: {
    title: 'Call someone',
    idle: 'Search by name, or dial a number.',
    loading: 'Loading contacts…',
    noMatchNumber: 'No contact has this number',
    noMatchNumberBody: 'You can still call it.',
    noMatchName: 'Only contacts with a phone number are listed.',
  },
};

const AddPersonOverlay: React.FC<IAddPersonOverlayProps> = ({
  open,
  callId,
  crmSourceId,
  suggestCrmSourceId,
  companyId,
  defaultCountry,
  onClose,
  mode = 'add',
  ticketPeople,
  recentCalls,
  onDial,
}) => {
  const classes = useAddPersonOverlayStyles();
  const copy = COPY[mode];
  const dispatch = useAppDispatch();
  const access = useAppSelector(CallScribeAccessSelector);
  const ticketCalls = useAppSelector(CallScribeCallsByTicketSelector);
  const declared = (access?.defaultCountryCode ?? '').trim().toUpperCase();
  const startCountry = (defaultCountry ?? (declared.length === 2 ? declared : 'US')) as CountryCode;

  const { country, national, applyRaw, chooseCountry } = useDialNumber(startCountry);
  const [nameQuery, setNameQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [sheetHeight, setSheetHeight] = useState<number | null>(null);
  const [keypadChoice, setKeypadChoice] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(0);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const fieldId = useId();
  const keypadId = useId();

  useEffect(() => {
    sessionRef.current += 1;
    if (!open) return;
    setBusy(false);
    setError('');
    setNameQuery('');
    applyRaw('');
    setSelectedKey(null);
    setActiveKey(null);
    setKeypadChoice(null);
  }, [open]);

  const directory = usePeopleDirectory({
    open,
    mode,
    crmSourceId,
    suggestCrmSourceId,
    companyId,
    ticketPeople,
    recentCalls: recentCalls ?? ticketCalls,
    country,
    national,
    nameQuery,
  });
  const people = flattenGroups(directory.groups);
  const selected = people.find(p => p.key === selectedKey) ?? null;
  const kind = nameQuery ? 'name' : national ? 'digits' : 'empty';
  const query = nameQuery || national;

  const [kindSeen, setKindSeen] = useState(kind);
  if (kind !== kindSeen) {
    setKindSeen(kind);
    if (kind === 'digits' && kindSeen === 'empty') setKeypadChoice(true);
    else if (kind !== 'digits') setKeypadChoice(null);
  }
  const shortSheet = sheetHeight !== null && sheetHeight > 0 && sheetHeight < SHORT_SHEET_PX;
  const keypadOpen = keypadChoice ?? (kind !== 'name' && !shortSheet);

  useEffect(() => {
    if (activeKey && people.some(p => p.key === activeKey)) return;
    const next = kind === 'name' && people.length > 0 ? people[0].key : null;
    if (next !== activeKey) setActiveKey(next);
  }, [people, activeKey, kind]);

  const parsed = national ? parsePhoneNumberFromString(national, country) : undefined;
  const numberValid = parsed?.isValid() ?? false;
  const tooShort = national.length > 0 && validatePhoneNumberLength(national, country) === 'TOO_SHORT';

  const e164Of = (phone: string): string | undefined => {
    const p = parsePhoneNumberFromString(phone, startCountry);
    return p?.isValid() ? p.number : undefined;
  };

  const request = ((): IAddCallParticipantRequest | undefined => {
    if (selected) {
      if (selected.kind === 'teammate' && typeof selected.accountId === 'number') {
        return { kind: 'teammate', accountId: selected.accountId };
      }
      if (selected.source === 'company' && selected.contactExternalId && typeof crmSourceId === 'number') {
        return { kind: 'contact', crmSourceId, contactExternalId: selected.contactExternalId };
      }
      const phone = e164Of(selected.phone);
      if (!phone) return undefined;
      const hintSource = selected.crmSourceId ?? crmSourceId;
      if (selected.contactExternalId && typeof hintSource === 'number') {
        return { kind: 'number', phone, crmSourceId: hintSource, contactExternalId: selected.contactExternalId };
      }
      return { kind: 'number', phone };
    }
    return numberValid && parsed ? { kind: 'number', phone: parsed.number } : undefined;
  })();

  const dialPick = ((): IDialPick | undefined => {
    if (selected) {
      const to = e164Of(selected.phone);
      if (!to) return undefined;
      return {
        to,
        contactExternalId: selected.contactExternalId ?? undefined,
        companyExternalId: selected.contactExternalId ? selected.companyExternalId ?? undefined : undefined,
        contactName: personCallName(selected),
      };
    }
    return numberValid && parsed ? { to: parsed.number } : undefined;
  })();

  const ready = mode === 'dial' ? Boolean(dialPick) : Boolean(request) && typeof callId === 'number';
  const who = selected ? personShortTitle(selected) : numberValid && parsed ? parsed.formatInternational() : '';

  const disabledReason = ((): string | undefined => {
    if (ready) return undefined;
    if (selected) return 'This number cannot be dialled';
    if (kind === 'digits') return tooShort ? 'Enter the full number' : 'Check the number';
    if (kind === 'name') return 'Pick someone from the list';
    return 'Pick someone or enter a number';
  })();

  const helper = ((): { text: string; tone: TFieldHelperTone } => {
    if (kind === 'name') return { text: `Showing names that match “${nameQuery.trim()}”.`, tone: 'info' };
    if (kind === 'digits') return numberHelper(national, country, parsed);
    return { text: directory.loading ? copy.loading : copy.idle, tone: 'info' };
  })();

  const clearPick = (): void => {
    setSelectedKey(null);
    setError('');
  };

  const pick = (person: IPerson): void => {
    setSelectedKey(person.key);
    setActiveKey(person.key);
    setError('');
  };

  const submit = (): void => {
    if (busy || !ready) return;
    if (mode === 'dial') {
      if (dialPick) onDial?.(dialPick);
      onClose();
      return;
    }
    if (!request || typeof callId !== 'number') return;
    const session = sessionRef.current;
    setBusy(true);
    setError('');
    void addParticipant(callId, request)
      .then(response => {
        const added = response?.data;
        if (added && typeof added.id === 'number') {
          dispatch(upsertParticipant({ callId, participant: added }));
        }
        if (session !== sessionRef.current) return;
        setBusy(false);
        onClose();
      })
      .catch((err: unknown) => {
        if (session !== sessionRef.current) return;
        setBusy(false);
        setError(extractApiErrorMessage(err, ADD_PERSON_FALLBACK_ERROR));
      });
  };

  const typeDigit = (key: string): void => {
    clearPick();
    if (key === '+') {
      setCountryMenuOpen(true);
      return;
    }
    if (key === 'Backspace') {
      if (nameQuery) setNameQuery(q => q.slice(0, -1));
      else applyRaw(national.slice(0, -1));
      return;
    }
    if (nameQuery) setNameQuery('');
    applyRaw(`${nameQuery ? '' : national}${key}`);
  };

  const onFieldKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (people.length === 0) return false;
      e.preventDefault();
      setActiveKey(stepActive(people, activeKey, e.key));
      return true;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const active = people.find(p => p.key === activeKey);
      if (active && active.key !== selectedKey) pick(active);
      else submit();
      return true;
    }
    return false;
  };

  const emptyState = ((): { icon: React.ReactElement; title: string; body: string } | null => {
    if (people.length > 0 || directory.loading) return null;
    if (kind === 'empty') {
      if (directory.failed.length > 0) return null;
      return {
        icon: <PersonAddAlt1OutlinedIcon aria-hidden />,
        title: 'No one here has a phone number yet',
        body: mode === 'add'
          ? 'Teammates and contacts without a phone number are hidden. Dial a number above, or add a mobile number to a profile.'
          : 'Contacts without a phone number are hidden. Dial a number above.',
      };
    }
    if (kind === 'digits') {
      return numberValid
        ? { icon: <SearchRoundedIcon aria-hidden />, title: copy.noMatchNumber, body: copy.noMatchNumberBody }
        : { icon: <SearchRoundedIcon aria-hidden />, title: 'No numbers match yet', body: 'Keep typing the number.' };
    }
    return {
      icon: <SearchRoundedIcon aria-hidden />,
      title: `No one matches “${nameQuery.trim()}”`,
      body: directory.anyoneDialable ? copy.noMatchName : 'Dial a number above instead.',
    };
  })();

  const listOpen = people.length > 0;

  return (
    <Sheet
      open={open}
      title={copy.title}
      onClose={onClose}
      initialFocusRef={fieldRef}
      onHeightChange={setSheetHeight}
      footer={(
        <ActionBar
          label={`Call ${who}`}
          busyLabel={`Adding ${who}…`}
          busy={busy}
          disabledReason={disabledReason}
          icon={<CallRoundedIcon />}
          onAction={submit}
        >
          {error ? <p className={classes.alert} role="alert">{error}</p> : null}
        </ActionBar>
      )}
    >
      <div className={classes.top}>
        <CountryDialField
          inputId={fieldId}
          inputRef={fieldRef}
          label="Search or dial"
          placeholder="Type a name or number"
          country={country}
          national={national}
          invalid={false}
          query={nameQuery}
          onQueryChange={q => { clearPick(); setNameQuery(q); }}
          onCountryChange={c => { clearPick(); chooseCountry(c); }}
          onNationalChange={raw => { clearPick(); applyRaw(raw); }}
          onKeyDown={onFieldKey}
          helper={helper.text}
          helperTone={helper.tone}
          combobox={{
            listId,
            expanded: listOpen,
            activeId: activeKey ? personOptionId(listId, activeKey) : undefined,
          }}
          countryMenuOpen={countryMenuOpen}
          onCountryMenuOpenChange={setCountryMenuOpen}
          helperAction={(
            <button
              type="button"
              className={classes.keypadToggle}
              aria-expanded={keypadOpen}
              aria-controls={keypadId}
              onMouseDown={e => { e.preventDefault(); }}
              onClick={() => { setKeypadChoice(!keypadOpen); }}
            >
              <DialpadRoundedIcon aria-hidden />
              Keypad
            </button>
          )}
        />
        <div
          id={keypadId}
          aria-hidden={keypadOpen ? undefined : true}
          className={`${classes.keypadWrap} ${keypadOpen ? '' : classes.keypadCollapsed}`}
        >
          <div className={classes.keypadInner}>
            <Keypad layout="dial" variant="sheet" onPress={typeDigit} />
          </div>
        </div>
      </div>

      <div className={classes.listArea}>
        {directory.failed.map(source => (
          <div key={source} className={classes.notice}>
            <span>{`${source} couldn't be loaded.`}</span>
            <button type="button" className={classes.noticeAction} onClick={directory.retry}>
              Try again
            </button>
          </div>
        ))}

        {directory.loading && people.length === 0 ? (
          <div data-testid="people-loading" aria-busy="true">
            {(mode === 'add' ? ['Teammates', 'Contacts'] : ['Contacts']).map((label, i) => (
              <div key={label} aria-hidden>
                <div className={classes.skeletonGroup}>{label}</div>
                {Array.from({ length: i === 0 ? 3 : 1 }, (_, n) => (
                  <div key={n} className={classes.skeleton} />
                ))}
              </div>
            ))}
          </div>
        ) : null}

        {listOpen ? (
          <PeopleList
            listId={listId}
            label={mode === 'add' ? 'People you can add' : 'People you can call'}
            groups={directory.groups}
            query={query}
            showCounts={kind !== 'empty'}
            activeKey={activeKey}
            selectedKey={selectedKey}
            focusable
            onPick={pick}
            onActiveChange={setActiveKey}
            onConfirm={submit}
            onTypeKey={key => {
              typeDigit(key);
              fieldRef.current?.focus();
            }}
          />
        ) : null}

        {emptyState ? (
          <div className={classes.empty}>
            <span className={classes.emptyRing}>{emptyState.icon}</span>
            <p className={classes.emptyTitle}>{emptyState.title}</p>
            <p className={classes.emptyBody}>{emptyState.body}</p>
          </div>
        ) : null}
      </div>
    </Sheet>
  );
};

export default AddPersonOverlay;
